import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, CalendarDays } from "lucide-react";
import api from "../lib/api";
import { useLang, pickTitle, pickSummary, pickBody, SECTIONS, formatDate, imgSrc } from "../lib/i18n";
import { LOGO_URL } from "../components/Masthead";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

const hideOnErr = (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.style.display = "none";
};

function Kicker({ children }) {
  return (
    <span className="font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-brk">
      {children}
    </span>
  );
}

function Byline({ a }) {
  return (
    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mt-2">
      {a.author}
    </p>
  );
}

export default function EpaperPage() {
  const { lang, t } = useLang();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    api
      .get("/articles?limit=120")
      .then((r) => setArticles(r.data.articles))
      .catch(() => setArticles([]));
  }, []);

  const dayArticles = (articles || []).filter((a) => a.published_at?.slice(0, 10) === date);
  const [lead, ...rest] = dayArticles;
  const secondary = rest.slice(0, 2);
  const columnStories = rest.slice(2, 8);
  const briefs = rest.slice(8, 18);

  const secLabel = (cat) => {
    const s = SECTIONS.find((x) => x.id === cat);
    return s ? (lang === "hi" ? s.hi : s.en) : cat;
  };

  const fmtDay = (d) => {
    const dt = new Date(d + "T00:00:00Z");
    return lang === "hi"
      ? dt.toLocaleDateString("hi-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })
      : dt.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
  };

  const dateISO = date + "T00:00:00Z";

  return (
    <div className="max-w-6xl mx-auto px-4 py-10" data-testid="epaper-page">
      <div className="no-print flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-hindi text-3xl md:text-4xl text-ink">{t.epaper}</h1>
          <p className="font-mono text-xs text-muted-foreground mt-1">{t.epaperSub}</p>
        </div>
        <div className="flex items-center gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="epaper-date"
                className="flex items-center gap-2 border border-ink/25 bg-white px-3 py-2 text-sm outline-none hover:border-deepgreen transition-colors"
              >
                <CalendarDays size={15} className="text-deepgreen" />
                {formatDate(date + "T00:00:00Z", lang)}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white" align="end">
              <Calendar
                mode="single"
                selected={new Date(date + "T00:00:00Z")}
                onSelect={(d) => {
                  if (!d) return;
                  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                  setDate(iso > today ? today : iso);
                }}
                disabled={(d) => d > new Date()}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <button
            data-testid="epaper-print"
            onClick={() => window.print()}
            className="flex items-center gap-2 bg-deepgreen text-paper px-5 py-2.5 text-sm font-semibold hover:bg-deepgreen-hover transition-colors"
          >
            <Printer size={15} /> {t.printDownload}
          </button>
        </div>
      </div>

      {!articles ? (
        <p className="text-center font-mono text-sm text-muted-foreground py-20">…</p>
      ) : dayArticles.length === 0 ? (
        <p className="text-center py-24 text-muted-foreground" data-testid="epaper-empty">
          {t.noEdition}
        </p>
      ) : (
        <div className="epaper-sheet bg-white border border-ink/40 shadow-[8px_8px_0_#D4AF37]" data-testid="epaper-sheet">
          {/* ===== MASTHEAD ===== */}
          <div className="px-6 md:px-10 pt-4 pb-0">
            <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground border-b border-ink/20 pb-2">
              <span>{fmtDay(date)}</span>
              <span className="hidden md:inline">vainorindia.com</span>
              <span>{lang === "hi" ? "पृष्ठ १" : "Page 1"}</span>
            </div>
            <div className="text-center pt-5 pb-4 border-b-4 border-double border-ink">
              <img src={LOGO_URL} alt="The Vainor India" className="h-16 w-auto mx-auto" />
              <h2 className="font-cinzel text-3xl md:text-5xl font-black tracking-[0.14em] text-deepgreen mt-2">
                THE VAINOR <span className="text-gold-accent">INDIA</span>
              </h2>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-2">
                {t.tagline} • {t.epaper} • {formatDate(dateISO, lang)}
              </p>
            </div>
          </div>

          {/* ===== LEAD STORY ===== */}
          <div className="px-6 md:px-10 pt-8">
            <article data-testid="epaper-lead">
              <Kicker>{secLabel(lead.category)}</Kicker>
              <h3 className="font-hindi text-4xl md:text-6xl leading-[1.12] mt-2 text-center">
                {pickTitle(lead, lang)}
              </h3>
              <p className="font-serif italic text-base md:text-lg text-muted-foreground mt-3 text-center max-w-3xl mx-auto leading-relaxed">
                {pickSummary(lead, lang)}
              </p>
              {lead.image_url && (
                <figure className="mt-6">
                  <img
                    src={imgSrc(lead.image_url)}
                    alt={pickTitle(lead, lang)}
                    onError={hideOnErr}
                    className="w-full aspect-[16/8] object-cover border border-ink/20"
                  />
                  {lead.image_caption && (
                    <figcaption className="font-mono text-[10px] text-muted-foreground mt-1.5 text-center">
                      {lead.image_caption}
                    </figcaption>
                  )}
                </figure>
              )}
              <div className="epaper-columns mt-6 text-[15px] leading-[1.85] text-inkgray text-justify">
                {pickBody(lead, lang)
                  .split(/\n+/)
                  .filter(Boolean)
                  .map((p, i) => (
                    <p key={i} className={i === 0 ? "epaper-dropcap" : "mt-3"}>
                      {p}
                    </p>
                  ))}
              </div>
              <div className="flex items-center justify-between mt-4 pb-6 border-b border-ink">
                <Byline a={lead} />
                <Link
                  to={`/article/${lead.id}`}
                  data-testid="epaper-lead-read"
                  className="font-mono text-[10px] uppercase tracking-widest text-deepgreen underline underline-offset-4"
                >
                  {t.readFull} →
                </Link>
              </div>
            </article>
          </div>

          {/* ===== SECONDARY PAIR ===== */}
          {secondary.length > 0 && (
            <div className="px-6 md:px-10 py-7 grid md:grid-cols-2 gap-0 md:divide-x md:divide-ink/25 border-b border-ink">
              {secondary.map((a, i) => (
                <article key={a.id} data-testid={`epaper-secondary-${i}`} className="py-5 md:py-0 md:px-7 first:md:pl-0 last:md:pr-0 border-b md:border-b-0 last:border-b-0 border-ink/20">
                  <Kicker>{secLabel(a.category)}</Kicker>
                  <h4 className="font-hindi text-2xl md:text-3xl leading-snug mt-1.5">{pickTitle(a, lang)}</h4>
                  {a.image_url && (
                    <img
                      src={imgSrc(a.image_url)}
                      alt={pickTitle(a, lang)}
                      onError={hideOnErr}
                      className="w-full aspect-[16/9] object-cover border border-ink/20 mt-3"
                    />
                  )}
                  <p className="text-sm text-inkgray leading-relaxed mt-3 text-justify">
                    {pickSummary(a, lang)}
                  </p>
                  <Byline a={a} />
                </article>
              ))}
            </div>
          )}

          {/* ===== COLUMN STORIES ===== */}
          {columnStories.length > 0 && (
            <div className="px-6 md:px-10 py-7 border-b border-ink">
              <div className="grid md:grid-cols-3 gap-0 md:divide-x md:divide-ink/25">
                {columnStories.map((a, i) => (
                  <article
                    key={a.id}
                    data-testid={`epaper-story-${i}`}
                    className="py-4 md:py-0 md:px-6 first:md:pl-0 last:md:pr-0 border-b md:border-b-0 last:border-b-0 border-ink/20"
                  >
                    <Kicker>{secLabel(a.category)}</Kicker>
                    <h4 className="font-hindi text-lg leading-snug mt-1">{pickTitle(a, lang)}</h4>
                    <p className="text-[13px] text-inkgray leading-relaxed mt-2 text-justify">
                      {pickSummary(a, lang)}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          )}

          {/* ===== संक्षिप्त / BRIEFS ===== */}
          {briefs.length > 0 && (
            <div className="px-6 md:px-10 py-7 border-b border-ink" data-testid="epaper-briefs">
              <h5 className="font-hindi text-xl border-b-2 border-gold inline-block pb-1 mb-5">
                {lang === "hi" ? "संक्षिप्त समाचार" : "News in Brief"}
              </h5>
              <div className="grid md:grid-cols-2 gap-x-10">
                {briefs.map((a) => (
                  <Link
                    key={a.id}
                    to={`/article/${a.id}`}
                    data-testid={`epaper-brief-${a.id}`}
                    className="group flex items-baseline gap-3 py-2.5 border-b border-dotted border-ink/30"
                  >
                    <span className="w-1.5 h-1.5 bg-brk shrink-0 translate-y-[-2px]" />
                    <span className="font-hindi text-[15px] leading-snug group-hover:text-deepgreen transition-colors">
                      {pickTitle(a, lang)}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* ===== IMPRINT FOOTER ===== */}
          <div className="px-6 md:px-10 py-4 flex flex-col md:flex-row items-center justify-between gap-1 text-[9px] font-mono uppercase tracking-wider text-muted-foreground">
            <span>© 2026 The Vainor India — A The Vainor Ventures firm • GSTIN 08KVFPS4146R1ZH</span>
            <span>
              {lang === "hi" ? "संपादकीय" : "Editorial"}: Baiya, Fatehgarh, Jaisalmer, Rajasthan – 345027 • Thevainorventures@gmail.com
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
